import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Users, PawPrint, UserCog, FileText, Calendar, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const subscription = dashboardData?.subscription || {};
  const upcomingSchedules = dashboardData?.upcoming_schedules || [];

  const statCards = [
    { icon: Users, label: 'Total Owners', value: stats.owners, color: 'sage', path: '/dashboard/owners' },
    { icon: PawPrint, label: 'Active Pets', value: stats.pets, color: 'terracotta', path: '/dashboard/pets' },
    { icon: UserCog, label: 'Doctors', value: stats.doctors, color: 'sage', path: '/dashboard/doctors' },
    { icon: FileText, label: 'Service Records', value: stats.diagnoses, color: 'terracotta', path: '/dashboard/diagnoses' },
  ];

  return (
    <div className="p-6 md:p-12" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl md:text-5xl font-heading font-bold text-sage-800 mb-2">Dashboard</h1>
        <p className="text-lg text-gray-600">Welcome back! Here's what's happening with your clinic.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 md:gap-6">
        {statCards.map((stat, idx) => (
          <Link
            key={idx}
            to={stat.path}
            className="col-span-1 sm:col-span-1 md:col-span-3"
            data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <Card className="bg-white rounded-3xl p-4 md:p-6 hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer border border-stone-100">
              <div className={`inline-flex p-2 md:p-3 rounded-2xl bg-${stat.color}-100 mb-2 md:mb-4`}>
                <stat.icon className={`w-5 h-5 md:w-6 md:h-6 text-${stat.color}-600`} />
              </div>
              <p className="text-xl md:text-3xl font-bold text-gray-800 mb-1">{stat.value}</p>
              <p className="text-xs md:text-sm text-gray-600 font-medium">{stat.label}</p>
            </Card>
          </Link>
        ))}

        <div className="col-span-12 md:col-span-8 row-span-2">
          <Card className="bg-white rounded-3xl p-8 h-full border border-stone-100 shadow-xl" data-testid="upcoming-schedules-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold text-sage-800">Upcoming Schedules</h2>
                <p className="text-sm text-gray-600">Next 7 days</p>
              </div>
              <Link to="/dashboard/schedules">
                <Button className="rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="view-all-schedules-button">
                  View All
                </Button>
              </Link>
            </div>
            <div className="space-y-4">
              {upcomingSchedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No upcoming schedules</p>
                </div>
              ) : (
                upcomingSchedules.map((schedule, idx) => (
                  <div
                    key={schedule.id}
                    className="flex items-center gap-4 p-4 bg-sage-50 rounded-2xl hover:bg-sage-100 transition-colors"
                    data-testid={`schedule-item-${idx}`}
                  >
                    <div className="flex-shrink-0 w-16 h-16 bg-sage-500 rounded-xl flex items-center justify-center text-white">
                      <Calendar className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{schedule.description}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(schedule.schedule_datetime).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        <div className="col-span-12 md:col-span-4 row-span-2">
          <Card className="bg-gradient-to-br from-sage-500 to-sage-700 rounded-3xl p-8 h-full text-white shadow-2xl" data-testid="subscription-card">
            <h2 className="text-2xl font-heading font-bold mb-6">Subscription Status</h2>
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-sm uppercase tracking-wide mb-2">Current Plan</p>
              <p className="text-3xl font-bold capitalize">{subscription.plan || 'Free'}</p>
            </div>
            <div className="space-y-4 mb-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Owners</span>
                  <span>{subscription.usage?.owners || 0} / {subscription.limits?.owners || 0}</span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-2">
                  <div
                    className="bg-white rounded-full h-2 transition-all"
                    style={{
                      width: `${Math.min(
                        ((subscription.usage?.owners || 0) / (subscription.limits?.owners || 1)) * 100,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Doctors</span>
                  <span>{subscription.usage?.doctors || 0} / {subscription.limits?.doctors || 0}</span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-2">
                  <div
                    className="bg-white rounded-full h-2 transition-all"
                    style={{
                      width: `${Math.min(
                        ((subscription.usage?.doctors || 0) / (subscription.limits?.doctors || 1)) * 100,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            {subscription.plan === 'free' && (
              <Link to="/dashboard/subscription">
                <Button className="w-full bg-white text-sage-700 hover:bg-sage-50 rounded-full py-6" data-testid="upgrade-plan-button">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Upgrade Plan
                </Button>
              </Link>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
